import Papa from "papaparse";

const FUND_RE = /^(G|F|C|S|I)( Fund)?$|^L (Income|20\d\d)$/i;
const money = (s) => {
  if (s == null) return null;
  const n = parseFloat(String(s).replace(/[$,\s]/g, ""));
  return isNaN(n) ? null : n;
};
export const normFund = (s) => {
  const t = String(s).trim().replace(/ Fund$/i, "");
  if (/^l\s*income$/i.test(t)) return "L Income";
  const m = t.match(/^L\s*(20\d\d)$/i);
  if (m) return `L ${m[1]}`;
  return t.toUpperCase();
};
const normDate = (s) => {
  const d = new Date(s);
  return isNaN(d) ? null : d.toISOString().slice(0, 10);
};

// Supports wide (Date, G Fund, C Fund, ..., Loan Balance, Agency Match) and long (Date, Fund, Shares, Balance) formats.
export function parseCsv(text, valueKind = "balance") {
  const { data, meta } = Papa.parse(text.trim(), { header: true, skipEmptyLines: true });
  const cols = meta.fields || [];
  const dateCol = cols.find((c) => /date|as of|period/i.test(c));
  const fundCol = cols.find((c) => /^fund$/i.test(c.trim()));
  const by = {};
  const get = (d) => (by[d] = by[d] || { date: d, holdings: {}, balances: {} });
  for (const row of data) {
    const d = normDate(dateCol ? row[dateCol] : new Date()) || new Date().toISOString().slice(0, 10);
    const s = get(d);
    if (fundCol) {
      const f = normFund(row[fundCol]);
      if (!FUND_RE.test(f)) continue;
      const sharesCol = cols.find((c) => /share|unit/i.test(c) && !/price/i.test(c));
      const balCol = cols.find((c) => /balance|value|amount/i.test(c));
      if (sharesCol && money(row[sharesCol]) != null) s.holdings[f] = money(row[sharesCol]);
      if (balCol && money(row[balCol]) != null) s.balances[f] = money(row[balCol]);
    } else {
      for (const c of cols) {
        const f = normFund(c);
        const v = money(row[c]);
        if (v == null) continue;
        if (FUND_RE.test(f)) (valueKind === "shares" ? s.holdings : s.balances)[f] = v;
        else if (/loan/i.test(c)) s.loan_balance = v;
        else if (/agency|match|automatic/i.test(c)) s.agency_match = (s.agency_match || 0) + v;
        else if (/employee/i.test(c)) s.employee_contrib = v;
      }
    }
  }
  return Object.values(by).filter((s) => Object.keys(s.holdings).length || Object.keys(s.balances).length);
}

export async function pdfToText(file) {
  const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  let text = "";
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const c = await page.getTextContent();
    text += c.items.map((it) => it.str).join(" ") + "\n";
  }
  return text;
}

const NUM = "\\$?\\s?([\\d,]+\\.\\d+)";

export function parseStatementText(text) {
  const t = text.replace(/\s+/g, " ");
  const snap = { holdings: {}, balances: {} };
  const dates = [...t.matchAll(/(\d{1,2}\/\d{1,2}\/\d{4})/g)].map((m) => normDate(m[1])).filter(Boolean).sort();
  snap.date = dates[dates.length - 1] || new Date().toISOString().slice(0, 10);
  const fundRe = new RegExp(`\\b(G Fund|F Fund|C Fund|S Fund|I Fund|L Income|L 20\\d\\d)\\b\\s+${NUM}\\s+${NUM}\\s+${NUM}`, "g");
  for (const m of t.matchAll(fundRe)) {
    const f = normFund(m[1]);
    const [a, b, c] = [money(m[2]), money(m[3]), money(m[4])];
    snap.holdings[f] = a;
    snap.balances[f] = Math.abs(a * b - c) < Math.max(1, c * 0.01) ? c : a * b;
  }
  if (!Object.keys(snap.balances).length) {
    const balRe = new RegExp(`\\b(G Fund|F Fund|C Fund|S Fund|I Fund|L Income|L 20\\d\\d)\\b[^$\\d]{0,40}${NUM}`, "g");
    for (const m of t.matchAll(balRe)) snap.balances[normFund(m[1])] = money(m[2]);
  }
  const loan = t.match(new RegExp(`(?:Outstanding )?Loan Balance[^$\\d]{0,30}${NUM}`, "i"));
  if (loan) snap.loan_balance = money(loan[1]);
  const match = t.match(new RegExp(`Agency Matching[^$\\d]{0,30}${NUM}`, "i"));
  const auto = t.match(new RegExp(`Agency Automatic(?: \\(1%\\))?[^$\\d]{0,30}${NUM}`, "i"));
  if (match || auto) snap.agency_match = (money(match?.[1]) || 0) + (money(auto?.[1]) || 0);
  const emp = t.match(new RegExp(`Employee(?: Contributions)?[^$\\d]{0,30}${NUM}`, "i"));
  if (emp) snap.employee_contrib = money(emp[1]);
  return Object.keys(snap.balances).length || Object.keys(snap.holdings).length ? [snap] : [];
}

export const SAMPLE_CSV = `Date,G Fund,F Fund,C Fund,S Fund,I Fund,L 2050,Loan Balance,Agency Match
03/31/2026,12000,4500,61000,22000,15000,0,3200,4100
06/30/2026,12350,4620,64800,23600,15900,0,2900,5300
09/30/2026,12700,4710,68900,24800,16600,0,2600,6500`;
